import * as redis from 'redis';
import { amorphicStatic, supertypeClass } from '@haventech/amorphic';
import { RedisClient } from 'redis';
import { LoggerSingleton, Category, PiiLevelEnum } from './logger/LoggerSingleton';

@supertypeClass({ toClient: false, toServer: false })
export class RedisCache {
  // Keep a single client/connection to each Redis server
  private static clientList: RedisClientArray;
  private Database: RedisDatabase;

  constructor(DbConnection?: RedisDatabase) {
    this.Database = DbConnection || RedisDatabase.AppDataCache;

    if (!RedisCache.clientList) {
      RedisCache.clientList = new RedisClientArray();
    }
  }

  private get client(): RedisClient {
    return RedisCache.clientList ? RedisCache.clientList[this.Database] : null;
  }

  static addClient(db: RedisDatabase, client: RedisClient): void {
    RedisCache.clientList[db] = client;
  }

  async connect(): Promise<void> {
    if (!this.client || !this.client.connected) {
      const port = amorphicStatic.config.nconf.get('REDIS_PORT');
      const host = amorphicStatic.config.nconf.get('REDIS_HOST');

      return new Promise<void>((resolve, reject) => {
        RedisCache.addClient(
          this.Database,
          redis.createClient(port, host, {
            db: amorphicStatic.config.nconf.get(this.Database.toString()),
          })
        );

        this.client.on('error', (error) => {
          LoggerSingleton.getInstance().error({
            module: 'RedisCache',
            function: 'connect',
            category: Category.availability,

            error: {
              name: error?.name,
              message: error?.message,
              stack: error?.stack,
              isHumanRelated: false,
            },
          });
        });

        this.client.auth(amorphicStatic.config.redisPassword, (error) => {
          if (error) {
            LoggerSingleton.getInstance().error({
              module: 'RedisCache',
              function: 'connect',
              category: Category.availability,

              error: {
                name: error?.name,
                message: error?.message,
                stack: error?.stack,
                isHumanRelated: false,
              },
            });
            reject(error);
          } else {
            resolve();
          }
        });
      });
    }
  }

  async fetchFromCache(key: string, fallback: (deferred, key: string) => void): Promise<any> {
    try {
      await this.connect();

      return new Promise<any>((resolve, reject) => {
        this.client.get(key, (error, result) => {
          if (error) {
            LoggerSingleton.getInstance().error({
              module: 'RedisCache',
              function: 'fetch',
              category: Category.request,

              error: {
                name: error?.name,
                message: error?.message,
                stack: error?.stack,
                isHumanRelated: false,
              },
            });
            reject(error);
          } else if (result) {
            // Cache hit
            LoggerSingleton.getInstance().info(
              {
                module: 'RedisCache',
                function: 'fetch',
                category: Category.request,
              },
              `Cache hit: ${key}`
            );
            resolve(JSON.parse(result));
          } else {
            // Cache miss
            LoggerSingleton.getInstance().info(
              {
                module: 'RedisCache',
                function: 'fetch',
                category: Category.request,
              },
              `Cache miss: ${key}`
            );

            // Would be better for fallback to return a Promise
            const deferred = defer<object>();
            fallback(deferred, key);

            deferred.promise
              .then((data) => {
                // Store in cache
                this.client.setex(key, 86400, JSON.stringify(data));
                resolve(data);
              })
              .catch(reject);
          }
        });
      });
    } catch (error) {
      // Most likely a Redis connection issue
      LoggerSingleton.getInstance().error({
        module: 'RedisCache',
        function: 'fetch',
        category: Category.request,

        error: {
          name: error?.name,
          message: error?.message,
          stack: error?.stack,
          isHumanRelated: false,
        },
      });

      // Use fallback function without attempting to cache
      const deferred = defer<object>();
      fallback(deferred, key);
      return deferred.promise;
    }
  }

  /**
   * Adds a new key,value pair to the redis cache with a specified 'time to live'
   * Returns a Promise<boolean> - True if the Key was added to the cache, False if the key
   * was not added
   * @param key - string on which to search
   * @param value - string to be cache
   * @param expiration - 'time to live' in seconds
   */
  async setStringInCacheWithExpiration(key: string, value: string, expiration: number): Promise<boolean> {
    try {
      await this.connect();

      const setKey = await this.client.setex(key, expiration, value);
      if (setKey !== null) {
        LoggerSingleton.getInstance().info(
          {
            module: 'RedisCache',
            function: 'setStringInCacheWithExpiration',
            category: Category.request,

            data: {
              key: key,
              expiration: expiration,
            },
          },
          `Key added to RedisCache`
        );
        return true;
      } else {
        LoggerSingleton.getInstance().info(
          {
            module: 'RedisCache',
            function: 'setStringInCacheWithExpiration',
            category: Category.request,

            data: {
              key: key,
              expiration: expiration,
            },
          },
          `Key not added to Cache`
        );
        return false;
      }
    } catch (error) {
      // Most likely a Redis connection issue
      LoggerSingleton.getInstance().error({
        module: 'RedisCache',
        function: 'setStringInCacheWithExpiration',
        category: Category.request,

        error: {
          name: error?.name,
          message: error?.message,
          stack: error?.stack,
          isHumanRelated: false,
          data: {
            key: key,
            expiration: expiration,
          },
        },
      });
      return false;
    }
  }

  /**
   * Retrieves the stored string for a key in the redis cache
   * as long as the time to live has not expired.
   * @param key - string on which to search
   */
  async getStringFromCache(key: string): Promise<string> {
    try {
      await this.connect();

      return new Promise<string>((resolve, reject) => {
        this.client.get(key, (error, result) => {
          if (error) {
            LoggerSingleton.getInstance().error({
              module: 'RedisCache',
              function: 'getStringFromCache',
              category: Category.request,

              error: {
                name: error?.name,
                message: error?.message,
                stack: error?.stack,
                isHumanRelated: false,
                data: {
                  key: key,
                },
              },
            });
            reject(error);
          } else if (result) {
            LoggerSingleton.getInstance().info(
              {
                module: 'RedisCache',
                function: 'getStringFromCache',
                category: Category.request,

                data: {
                  key: key,
                },
              },
              `Cache hit. Key found`
            );
            resolve(result);
          } else {
            LoggerSingleton.getInstance().info(
              {
                module: 'RedisCache',
                function: 'getStringFromCache',
                category: Category.request,

                data: {
                  key: key,
                },
              },
              `Cache miss. Key does not exist`
            );
            resolve(null);
          }
        });
      });
    } catch (error) {
      LoggerSingleton.getInstance().error(
        {
          module: 'RedisCache',
          function: 'getStringFromCache',
          category: Category.request,

          error: {
            name: error?.name,
            message: error?.message,
            stack: error?.stack,
            isHumanRelated: false,
            data: {
              key: key,
            },
          },
        },
        `Unable to get key from RedisCache`
      );
      return null;
    }
  }

  /**
   * Removes key from redis cache regardless of expiration time set for the key
   * @param key to be deleted from cache
   */
  async deleteFromCache(key: string): Promise<boolean> {
    try {
      await this.connect();
      const numOfDeleted = this.client.del(key);

      if (numOfDeleted) {
        LoggerSingleton.getInstance().info(
          {
            module: 'RedisCache',
            function: 'delete',
            category: Category.request,

            data: {
              key: key,
              numberOfItemsDeleted: numOfDeleted,
            },
          },
          `Key deleted from RedisCache`
        );
        return true;
      } else {
        LoggerSingleton.getInstance().info(
          {
            module: 'RedisCache',
            function: 'delete',
            category: Category.request,

            data: {
              key: key,
              numberOfItemsDeleted: numOfDeleted,
            },
          },
          `Key not deleted from RedisCache`
        );
        //can this be true? developer may be clearing out a key before inserting a new value
        return false;
      }
    } catch (error) {
      LoggerSingleton.getInstance().error(
        {
          module: 'RedisCache',
          function: 'delete',
          category: Category.request,
          context: { piiLevel: PiiLevelEnum.s2 },
          error: {
            isHumanRelated: false,
            name: error?.name,
            message: error?.message,
            stack: error?.stack,
            data: {
              key: key,
            },
          },
        },
        `Key not deleted from RedisCache`
      );
      return false;
    }
  }

  /**
   * setter used for testing purposes only
   * @param client
   */
  public setTestClient(client: RedisClient, DbConnection?: RedisDatabase): void {
    const connection = DbConnection || RedisDatabase.AppDataCache;
    RedisCache.addClient(connection, client);
  }
}

function defer<T>() {
  let resolve, reject;
  const promise = new Promise<T>((r1, r2) => {
    [resolve, reject] = [r1, r2];
  });
  return { resolve, reject, promise };
}

export enum RedisDatabase {
  SessionCache = 'redisSessionCacheDB',
  AppDataCache = 'redisAppDataCacheDB',
}

export class RedisClientArray {
  [key: string]: RedisClient;
}
